"use client";

import "./contactArea.css";
import Image from "next/image";
import { useFormik } from "formik";
import { FiSend } from "react-icons/fi";
import { useEffect, useState } from "react";
import { trackLead } from "src/utils/func/trackLead";
import { countries } from "src/utils/mock/countryCode";
import PrimaryButton from "src/components/atoms/PrimaryButton";
import { Mail, User, MapPin, Wallet, PhoneCall, Briefcase, MessageSquare, SendHorizontal } from "lucide-react";
import { services, validationSchema } from "src/utils/mock/contactValidation";

// ======================
// Move FloatField OUTSIDE
// ======================
const FloatField = ({ as = "input", type = "text", name, label, icon: Icon, rows, className = "", formik }) => {
  const Tag = as;
  const value = formik.values[name];
  const error = formik.errors[name];
  const touched = formik.touched[name];

  return (
    <div className={className}>
      <div className="relative">
        <Tag
          type={as === "input" ? type : undefined}
          rows={rows}
          name={name}
          id={name}
          placeholder=" "
          value={value}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          className={`peer w-full rounded-[10px] border bg-background-secondary !pr-4 !pl-[42px] !pt-[20px] !pb-[6px] !text-[11.5px] xl:!text-[13.5px] text-text-primary outline-none transition-all duration-200 placeholder-transparent
            ${touched && error ? "border-red-400/60" : "border-border-primary"}
            focus:border-[#6F59FF] focus:ring-[3px] focus:ring-[#6F59FF]/12 focus:bg-[#6F59FF]/[0.03]
            ${as === "textarea" ? "resize-none !pt-[24px]" : "h-[54px]"}`}
        />

        {Icon && (
          <Icon
            className={`pointer-events-none absolute left-[14px] ${
              as === "textarea" ? "top-[22px]" : "top-1/2 -translate-y-1/2"
            } !h-[15px] !w-[15px] text-text-secondary peer-focus:text-primary transition-colors duration-200`}
          />
        )}

        <label
          htmlFor={name}
          className="pointer-events-none absolute left-[42px] top-[16px] text-[13px] text-text-secondary transition-all duration-200
            peer-focus:top-[8px] peer-focus:!text-[10px] peer-focus:text-primary peer-focus:tracking-[0.4px]
            peer-[:not(:placeholder-shown)]:top-[8px] peer-[:not(:placeholder-shown)]:!text-[10px] peer-[:not(:placeholder-shown)]:tracking-[0.4px]"
        >
          {label}
        </label>
      </div>
      {touched && error && <p className="!mt-[5px] text-[11px] text-red-400">{error}</p>}
    </div>
  );
};

export default function ContactSection() {
  const [isLoading, setIsLoading] = useState(false);
  const [isGeoLoading, setIsGeoLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const formik = useFormik({
    initialValues: {
      full_name: "",
      email_address: "",
      phone_number: "",
      services: "",
      message: "",
      country_code: "+91",
      budget: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);

      const formData = new URLSearchParams({
        name: values.full_name.trim(),
        email: values.email_address.trim(),
        phone: values.phone_number.trim(),
        our_service: values.services,
        message: values.message.trim(),
        resume: "",
        country_code: values.country_code,
        budget: values.budget,
      });

      try {
        const response = await fetch("https://api-tycho.digitalseller.in/contact-us", {
          method: "PUT",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        });

        const res = await response.json();

        if (res?.status === true) {
          trackLead({
            formName: "Homepage Contact Form",
            service: values.services,
          });
          setSubmitted(true);
          formik.resetForm();

          setTimeout(() => {
            setSubmitted(false);
          }, 6000);
        } else {
          alert(res?.message || "Something went wrong. Please try again.");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        alert("Failed to send message. Please check your internet connection.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const detectCountry = async () => {
      setIsGeoLoading(true);

      try {
        const res = await fetch("https://geolocation-db.com/json/", {
          cache: "no-store",
        });
        const data = await res.json();

        if (data.country_code) {
          const matched = countries.find((c) => c.short_name.toUpperCase() === data.country_code.toUpperCase());

          if (matched?.phone_code) {
            formik.setFieldValue("country_code", `+${matched.phone_code}`);
            setIsGeoLoading(false);
            return;
          }
        }
      } catch (e) {
        console.warn("Primary geolocation failed");
      }

      try {
        const fallbackRes = await fetch("https://ip-api.com/json");
        const fallbackData = await fallbackRes.json();

        if (fallbackData.countryCode) {
          const matched = countries.find((c) => c.short_name.toUpperCase() === fallbackData.countryCode.toUpperCase());

          if (matched?.phone_code) {
            formik.setFieldValue("country_code", `+${matched.phone_code}`);
          }
        }
      } catch (err) {
        console.error("Geolocation fallback failed");
      } finally {
        setIsGeoLoading(false);
      }
    };

    detectCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactItems = [
    {
      icon: PhoneCall,
      label: "+91 971 769 9480",
      sub: "Business Enquiry",
      href: "tel:+919717699480",
    },
    {
      icon: PhoneCall,
      label: "+91 920 567 1059",
      sub: "Support",
      href: "tel:+919205671059",
    },
    {
      icon: Mail,
      label: "contact@tychotechnologies.com",
      sub: "Email Us",
      href: "mailto:contact@tychotechnologies.com",
    },
  ];

  return (
    <section className="relative overflow-hidden px-6! sm:px-12! lg:px-10! mt-10! pb-5!">
      <div className="relative mx-auto !w-full overflow-hidden !rounded-2xl  !border !border-border-primary shadow-[0_30px_100px_-40px_rgba(0,0,0,0.35)] backdrop-blur-xl mb-5!">
        <div className="grid !grid-cols-1 lg:!grid-cols-[40%_40%_20%] !bg-background-primary/80">
          {/* ================= LEFT SIDE ================= */}
          <div className="relative overflow-hidden !p-7 sm:!p-10 lg:!p-12">
            <div className="relative z-10">
              <div className="!mb-5 inline-flex items-center !gap-2 !px-3 !py-1.5">
                <span className="!text-[14px] !font-bold uppercase !tracking-[1.6px] text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
                  Let&apos;s Build Together
                </span>
              </div>

              <h2 className="!text-[32px] !font-semibold !leading-[1.12] !tracking-[-1.5px] !text-text-primary sm:!text-[40px] lg:!text-[42px]">
                Have a project
                <span className="bg-gradient-to-r from-[#6F59FF] to-[#4FA8FF] bg-clip-text text-transparent"> in mind?</span>
              </h2>

              <p className="!mt-5 !max-w-[510px] !text-[14px] !leading-7 !text-text-secondary sm:!text-[15px]">
                Tell us what you&apos;re building and what you need. Our team will get back to you with a clear plan, timeline and next steps.
              </p>

              <div className="!mt-10 !space-y-3">
                {contactItems.map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    className="group flex items-center !gap-4 !rounded-2xl !border !border-border-primary !bg-background-secondary/50 !p-4 transition-all duration-300 hover:-translate-y-0.5 hover:!border-[#6F59FF]/30 hover:!bg-[#6F59FF]/5"
                  >
                    <div className="flex !h-11 !w-11 shrink-0 items-center justify-center !rounded-2xl !border !border-[#6F59FF]/20 !bg-[#6F59FF]/10">
                      <item.icon className="!h-4 !w-4 !text-primary" />
                    </div>

                    <div className="min-w-0">
                      <p className="break-all !text-[13.5px] lg:!text-[16.5px] !font-medium !text-text-primary transition-colors group-hover:!text-primary">{item.label}</p>
                      <p className="!mt-1 !text-[11px] lg:!text-[13.5px] !text-text-secondary">{item.sub}</p>
                    </div>
                  </a>
                ))}

                <a
                  href="https://www.google.com/maps/place/Tycho+Technologies+%7C+Mobile+Apps,+OTT,+ROKU+%26+Web+Development+Company+in+India/@28.6297619,77.3766279,17z/data=!3m1!4b1!4m14!1m7!3m6!1s0x390ce40a1a48d491:0x773396a1d869a95f!2sTycho+Technologies+%7C+Mobile+Apps,+OTT,+ROKU+%26+Web+Development+Company+in+India!8m2!3d28.6297572!4d77.3792028!16s%2Fg%2F11g889jrhd!3m5!1s0x390ce40a1a48d491:0x773396a1d869a95f!8m2!3d28.6297572!4d77.3792028!16s%2Fg%2F11g889jrhd?entry=ttu&g_ep=EgoyMDI2MDQwOC4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start !gap-4 !rounded-2xl !border !border-border-primary !bg-background-secondary/50 !p-4 transition-all duration-300 hover:-translate-y-0.5 hover:!border-[#6F59FF]/30 hover:!bg-[#6F59FF]/5"
                >
                  <div className="flex !h-11 !w-11 shrink-0 items-center justify-center !rounded-2xl !border !border-[#6F59FF]/20 !bg-[#6F59FF]/10">
                    <MapPin className="!h-4 !w-4 !text-primary" />
                  </div>

                  <p className="!pt-1 !text-[13.5px] lg:!text-[16.5px] !font-medium !leading-5 !text-text-secondary transition-colors group-hover:!text-text-primary">
                    Office No. 305, 3rd Floor, H-32, Sector-63, Noida, Uttar Pradesh - 201301
                  </p>
                </a>
              </div>
            </div>
          </div>

          {/* ================= MIDDLE - Form ================= */}
          <div className="relative !p-6 sm:!p-10 rounded-l-xl !bg-surface-primary">
            <div className="pointer-events-none absolute !-right-24 !-top-24 !h-64 !w-64 rounded-full !bg-[#6F59FF]/15 blur-[90px]" />
            <div className="pointer-events-none absolute !-bottom-24 !-left-24 !h-64 !w-64 rounded-full !bg-[#4FA8FF]/10 blur-[90px]" />

            {submitted ? (
              <div className="flex !min-h-[500px] flex-col items-center justify-center !text-center">
                <div className="relative !mb-6 flex !h-20 !w-20 items-center justify-center !rounded-full !border !border-green-400/20 !bg-green-400/10">
                  <div className="absolute inset-0 rounded-full !bg-green-400/10 blur-xl" />
                  <SendHorizontal className="relative !h-8 !w-8 !text-green-400" />
                </div>

                <h3 className="!text-2xl !font-semibold !tracking-tight !text-text-primary">Message sent!</h3>

                <p className="!mt-2 !max-w-[320px] !text-sm !leading-6 !text-text-secondary">Thanks for reaching out. Our team will get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={formik.handleSubmit} className="mx-auto !w-full !max-w-[680px]">
                <div className="!mb-8">
                  <h3 className="!mt-2 !text-[24px] !font-semibold !tracking-[-0.8px] !text-text-primary sm:!text-[28px]">Tell us about your project</h3>
                  <p className="!mt-2 !text-[13px] !leading-6 !text-text-secondary">Fill in a few details and we&apos;ll take it from there.</p>
                </div>

                <div className="!space-y-4">
                  <div className="grid !grid-cols-1 !gap-4 md:!grid-cols-2">
                    <FloatField name="full_name" label="Full Name *" icon={User} formik={formik} />
                    <FloatField name="email_address" type="email" label="Email Address *" icon={Mail} formik={formik} />
                  </div>

                  <div className="grid !grid-cols-1 !gap-4 md:!grid-cols-2">
                    {/* Phone */}
                    <div>
                      <div className="relative flex !h-[56px] overflow-hidden rounded-[10px] !border !border-border-primary bg-background-secondary transition-all focus-within:!border-[#6F59FF] focus-within:!ring-4 focus-within:!ring-[#6F59FF]/10">
                        <div className="flex items-center !pl-4">
                          <PhoneCall className="!h-4 !w-4 !text-text-secondary" />
                        </div>

                        <select
                          name="country_code"
                          value={formik.values.country_code}
                          onChange={formik.handleChange}
                          disabled={isGeoLoading}
                          className="!border-r !border-border-primary !bg-transparent !px-2 !text-xs !text-text-secondary !outline-none"
                        >
                          {countries
                            .filter((c) => c.phone_code && c.phone_code !== "0")
                            .map((c) => (
                              <option key={c.id} value={`+${c.phone_code}`}>
                                +{c.phone_code}
                              </option>
                            ))}
                        </select>

                        <input
                          type="tel"
                          name="phone_number"
                          value={formik.values.phone_number}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            formik.setFieldValue("phone_number", digits);
                          }}
                          onBlur={formik.handleBlur}
                          placeholder="Phone number *"
                          maxLength={15}
                          className="min-w-0 flex-1 !bg-transparent !px-4 !text-sm !text-text-primary !outline-none placeholder:!text-text-secondary"
                        />
                      </div>

                      {formik.touched.phone_number && formik.errors.phone_number && <p className="!mt-1 !text-[11px] !text-red-400">{formik.errors.phone_number}</p>}
                    </div>

                    {/* Service */}
                    <div>
                      <div className="relative flex !h-[56px] items-center rounded-[10px] !border !border-border-primary bg-background-secondary transition-all focus-within:!border-[#6F59FF] focus-within:!ring-4 focus-within:!ring-[#6F59FF]/10">
                        <Briefcase className="!ml-4 !mr-3 !h-4 !w-4 shrink-0 !text-text-secondary" />

                        <select
                          name="services"
                          value={formik.values.services}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="!h-full !w-full bg-background-secondary !pr-4 !text-sm !text-text-secondary !outline-none rounded-[10px]"
                        >
                          {services.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {formik.touched.services && formik.errors.services && <p className="!mt-1 !text-[11px] !text-red-400">{formik.errors.services}</p>}
                    </div>
                  </div>

                  <FloatField name="budget" label="Estimated Budget (optional)" icon={Wallet} formik={formik} />

                  <FloatField as="textarea" name="message" rows={5} label="Tell us about your project *" icon={MessageSquare} formik={formik} />
                </div>

                <div className="!mt-6 flex flex-col !gap-4 !border-t !border-border-primary !pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center !gap-2 !text-[11.5px] !text-text-secondary">
                    <span className="!h-2 !w-2 rounded-full !bg-green-400" />
                    Usually responds within 24 hours
                  </div>

                  <div className="flex justify-center">
                    <PrimaryButton size="lg" type="submit" icon={<FiSend />} className="!px-5 !py-3" loading={isLoading}>
                      Discuss Your Project
                    </PrimaryButton>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* ================= RIGHT SIDE - Image ================= */}
          <div className="relative hidden lg:block overflow-hidden">
            <Image
              src="/images/serviceBanners/newContactImage.webp"
              alt="Contact illustration"
              fill
              className="object-cover object-bottom-right dark:hidden"
              sizes="20vw"
              priority
            />
            <Image
              src="/images/serviceBanners/newContactImageB.webp"
              alt="Contact illustration"
              fill
              className="object-cover object-right hidden dark:block"
              sizes="20vw"
              priority
            />
          </div>
        </div>
      </div>
    </section>
  );
}
